import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Image,
  Input,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import './avatar-picker.scss';

export type AvatarOutputMode = 'base64' | 'file';

export interface IAvatarItem {
  id: number;
  src: string;
  alt: string;
}

export interface IAvatarValue {
  src: string;
  type: 'avatar' | 'photo';
  file?: File;
  confirmed?: boolean;
}

export interface IAvatarPickerProps {
  value: IAvatarValue;
  avatarList: IAvatarItem[];
  outputMode?: AvatarOutputMode;
  onChange: (avatar: IAvatarValue) => void;
  onRemove: () => void;
  onConfirm?: () => void;
  showConfirm?: boolean;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
  });
}

export const AvatarPicker = ({
  value,
  avatarList,
  outputMode = 'base64',
  onChange,
  onRemove,
  onConfirm,
  showConfirm = true,
}: IAvatarPickerProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleZoomChange = useCallback((newZoom: number) => {
    setZoomLevel(Math.min(Math.max(newZoom, 1), 2));
  }, []);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setZoomLevel(1);
      setIsZoomed(true);
      event.target.value = '';

      if (outputMode === 'file') {
        const src = URL.createObjectURL(file);
        onChange({ src, type: 'photo', file, confirmed: false });
        return;
      }

      const base64 = await fileToBase64(file);
      onChange({ src: base64, type: 'photo', confirmed: false });
    },
    [outputMode, onChange],
  );

  const handleAvatarSelect = useCallback(
    async (item: IAvatarItem) => {
      setZoomLevel(1);
      setIsZoomed(false);
      if (outputMode === 'file') {
        const resp = await fetch(item.src);
        const blob = await resp.blob();
        const file = new File([blob], 'avatar.svg', {
          type: blob.type || 'image/svg+xml',
        });
        onChange({ src: item.src, type: 'avatar', file, confirmed: true });
        return;
      }
      onChange({ src: item.src, type: 'avatar', confirmed: true });
    },
    [outputMode, onChange],
  );

  const handleRemove = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setZoomLevel(1);
    setIsZoomed(false);
    onRemove();
  }, [onRemove]);

  const handleConfirm = useCallback(() => {
    setIsZoomed(false);
    onChange({ ...value, confirmed: true });
    onConfirm?.();
  }, [value, onChange, onConfirm]);

  const isPhoto = value.type === 'photo';

  return (
    <Flex direction='column' className='avatar-section'>
      <div className='avatar-preview'>
        <Box overflow='hidden' borderRadius='50%'>
          <Image
            src={value.src}
            alt=''
            style={{
              transform: `scale(${zoomLevel})`,
              transition: 'transform 0.2s ease-in-out',
            }}
          />
        </Box>
        {isPhoto && (
          <button type='button' className='remove-btn' onClick={handleRemove}>
            ×
          </button>
        )}
      </div>

      {isPhoto && isZoomed && (
        <Flex
          className='avatar-zoom'
          alignItems='center'
          justifyContent='space-between'
        >
          <IconButton
            aria-label='Zoom in'
            icon={<span>+</span>}
            onClick={() => handleZoomChange(zoomLevel + 0.1)}
            size='sm'
            variant='outline'
            isDisabled={zoomLevel >= 2}
            borderRadius='full'
          />
          <Box flex='1' mx={3}>
            <Slider
              aria-label='zoom-control'
              value={zoomLevel}
              min={1}
              max={2}
              size='md'
              step={0.1}
              onChange={handleZoomChange}
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </Box>
          <IconButton
            aria-label='Zoom out'
            icon={<span>-</span>}
            onClick={() => handleZoomChange(zoomLevel - 0.1)}
            size='sm'
            variant='outline'
            isDisabled={zoomLevel <= 1}
            borderRadius='full'
          />
        </Flex>
      )}

      <Flex direction='row' gap='10px' className='avatar-list'>
        {avatarList.map(item => (
          <Image
            key={item.id}
            src={item.src}
            alt={item.alt}
            className='avatar-list-item'
            onClick={() => handleAvatarSelect(item)}
            width={70}
            height={70}
          />
        ))}
        <Input
          ref={fileInputRef}
          type='file'
          id='avatar-upload'
          accept='image/*'
          onChange={handleFileSelected}
          display='none'
        />
        <FormLabel htmlFor='avatar-upload' cursor='pointer' mb={0}>
          <Box className='avatar-list-item avatar-upload-btn' width={70} height={70}>
            +
          </Box>
        </FormLabel>
      </Flex>

      {isPhoto && !value.confirmed ? (
        <>
          <p>{t('settings.choose-avatar-file')}</p>
          {showConfirm ? (
            <Button
              type='button'
              onClick={handleConfirm}
              className='btn-confirm'
            >
              {t('common.confirm')}
            </Button>
          ) : null}
        </>
      ) : !isPhoto ? (
        <>
          <p>{t('settings.choose-avatar')}</p>
          <p>{t('common.or')}</p>
          <p>{t('settings.add-profile-picture')}</p>
        </>
      ) : null}
    </Flex>
  );
};

export default AvatarPicker;
